// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import {
    Form,
    FormField,
    FormFieldText,
    Icon,
    Input,
    StatusMessage,
    Tooltip,
    Translate,
    useTranslate,
} from '@plesk/ui-library';
import { RootState } from 'admin/core/store';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import * as failoverDomainActions from 'common/modules/failoverDomain/actions';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import { getProcessedErrors } from 'common/modules/app/formErrors/selectors';
import AsyncSelectInput from 'common/components/Select/AsyncSelectInput';
import { createOptionsLoader } from 'common/components/Select/helpers';
import { RemoteStorageTypes } from 'common/api/resources/StorageType';
import {
    ILocationResponse,
    locations,
} from 'common/api/resources/Location';
import {
    IStorageResponse,
    storages,
} from 'common/api/resources/Storage';
import { IFailoverDomainResponse } from 'common/api/resources/FailoverDomain';
import { Button } from 'admin/common/components/Button/Button';
import {
    ICONS,
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { ISelectOption } from 'common/components';
import StepBreadcrumb from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/StepBreadcrumb';
import ComputeResourceStep from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/ComputeResourceStep';
import IpBlockStep from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/IpBlockStep';
import {
    DialogOverlay,
    DialogHeader,
    DialogHeaderLeft,
    DialogTitle,
    DialogBody,
    FormPanel,
    ContentPanel,
    ContentHeader,
    ButtonGroup,
    Placeholder,
    SearchContainer,
} from 'admin/failoverDomain/containers/FailoverDomainCreateDialog/Styles';

export interface IFailoverDomainCreateDialogOwnProps {
    editItem?: IFailoverDomainResponse;
    onClose: () => void;
    onSaved: () => void;
}

export type FailoverDomainCreateDialogProps =
    IFailoverDomainCreateDialogOwnProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

interface IFormValues {
    name: string;
    description: string;
    storage_id: number;
    location_id: number;
}

// Fields rendered inside the <Form>, so their validation errors are shown in place by the
// FormField itself. Every other server error key (e.g. failover_domain_id, compute_resource_ids,
// ip_block_ids) has no matching field here — the CR/IP-block selection lives in the side panels —
// so those must be surfaced explicitly, otherwise the request silently fails with no feedback.
const FORM_FIELD_ERROR_KEYS = ['name', 'description', 'storage_id', 'location_id'];

export const FailoverDomainCreateDialog: React.FC<FailoverDomainCreateDialogProps> = ({
    editItem,
    onClose,
    onSaved,
    isSubmitting,
    formErrors,
    failoverDomainActions: {
        createFailoverDomain,
        updateFailoverDomain,
    },
    formErrorsActions: {
        clearFormErrors,
    },
}) => {
    const translate = useTranslate();
    const isEditMode = !!editItem;

    const generalErrorMessages = Object.entries(formErrors)
        .filter(([key]) => !FORM_FIELD_ERROR_KEYS.includes(key))
        .flatMap(([, value]) => (Array.isArray(value) ? value : [value]))
        .filter((message): message is string => typeof message === 'string' && message.length > 0);

    const [formValues, setFormValues] = React.useState<IFormValues>({
        name: editItem?.name ?? '',
        description: editItem?.description ?? '',
        storage_id: editItem?.storage?.id ?? 0,
        location_id: editItem?.location?.id ?? 0,
    });

    const [storageOption, setStorageOption] = React.useState<ISelectOption | null>(
        editItem?.storage ? { label: editItem.storage.name, value: editItem.storage.id } : null
    );
    const [locationOption, setLocationOption] = React.useState<ISelectOption | null>(
        editItem?.location ? { label: editItem.location.name, value: editItem.location.id } : null
    );

    const [currentStep, setCurrentStep] = React.useState(1);
    const [visitedStep2, setVisitedStep2] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [selectedCrIds, setSelectedCrIds] = React.useState<number[]>(editItem?.compute_resource_ids ?? []);
    const [selectedIpBlockIds, setSelectedIpBlockIds] = React.useState<number[]>([]);

    React.useEffect(() => () => {
        clearFormErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasStorageAndLocation = formValues.storage_id > 0 && formValues.location_id > 0;

    const canProceedToStep2 = formValues.name.trim().length > 0
        && hasStorageAndLocation
        && selectedCrIds.length >= 2;

    const canSaveDirectly = isEditMode
        && formValues.name.trim().length > 0
        && hasStorageAndLocation
        && selectedCrIds.length === 0;

    const loadStorageOptions = React.useMemo(() => createOptionsLoader(
        storages.list,
        (storage: IStorageResponse) => ({
            label: storage.name,
            value: storage.id,
        }),
        true,
        { type: RemoteStorageTypes }
    ), []);

    const loadLocationOptions = React.useMemo(() => createOptionsLoader(
        locations.list,
        (location: ILocationResponse) => ({
            label: location.name,
            value: location.id,
        })
    ), []);

    const createSelectHandler = (
        field: 'storage_id' | 'location_id',
        setOption: (opt: ISelectOption | null) => void
    ) => (option: ISelectOption | null) => {
        setOption(option);
        setFormValues(prev => ({ ...prev, [field]: option ? Number(option.value) : 0 }));
        setSelectedCrIds([]);
    };

    const handleStorageChange = createSelectHandler('storage_id', setStorageOption);
    const handleLocationChange = (option: ISelectOption | null) => {
        setLocationOption(option);
        setFormValues(prev => ({
            ...prev,
            location_id: option ? Number(option.value) : 0,
            storage_id: 0,
        }));
        setStorageOption(null);
        setSelectedCrIds([]);
    };

    const handleNext = () => {
        if (canProceedToStep2) {
            setSearchQuery('');
            setVisitedStep2(true);
            setCurrentStep(2);
        }
    };

    const handleBack = () => {
        setSelectedIpBlockIds([]);
        setSearchQuery('');
        setCurrentStep(1);
    };

    const handleSubmit = async () => {
        // Include selections when the user went through step 2 (normal edit flow)
        // or when saving directly from step 1 after emptying all CRs — otherwise the
        // emptied list is silently dropped from the PATCH payload.
        const includeSelections = visitedStep2 || canSaveDirectly;

        const apiCall = isEditMode && editItem
            ? updateFailoverDomain(editItem.id, {
                name: formValues.name,
                description: formValues.description || undefined,
                ...(includeSelections ? {
                    compute_resource_ids: selectedCrIds,
                    ip_block_ids: selectedIpBlockIds,
                } : {}),
            })
            : createFailoverDomain({
                name: formValues.name,
                description: formValues.description || undefined,
                storage_id: formValues.storage_id,
                location_id: formValues.location_id,
                compute_resource_ids: selectedCrIds,
                ip_block_ids: selectedIpBlockIds,
            });

        try {
            await apiCall;
            onSaved();
        } catch {
            // Form errors are set by redux middleware
        }
    };

    const renderFooterButtons = () => {
        if (currentStep === 1 && canSaveDirectly) {
            return (
                <Button
                    intent={INTENT_TYPE.PRIMARY}
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    data-cy="failover-domain-submit-btn"
                >
                    <Translate content="failoverDomain.create.form.save" />
                </Button>
            );
        }

        if (currentStep === 1) {
            return (
                <Button
                    intent={INTENT_TYPE.PRIMARY}
                    onClick={handleNext}
                    disabled={!canProceedToStep2}
                    data-cy="failover-domain-next-btn"
                >
                    <Translate content="failoverDomain.create.form.next" />
                </Button>
            );
        }

        return (
            <>
                <Button
                    onClick={handleBack}
                    data-cy="failover-domain-back-btn"
                >
                    <Translate content="failoverDomain.create.form.back" />
                </Button>
                <Button
                    intent={INTENT_TYPE.PRIMARY}
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    data-cy="failover-domain-submit-btn"
                >
                    <Translate content={isEditMode
                        ? 'failoverDomain.create.form.save'
                        : 'failoverDomain.create.form.create'
                    } />
                </Button>
            </>
        );
    };

    return (
        <DialogOverlay data-cy="failover-domain-create-dialog">
            <DialogHeader>
                <DialogHeaderLeft>
                    <Button
                        ghost={true}
                        icon={ICONS.CROSS_MARK}
                        onClick={onClose}
                        data-cy="failover-domain-dialog-close"
                    />
                    <DialogTitle>
                        {isEditMode ? (
                            <>
                                <Translate content="failoverDomain.title" />
                                {' > '}
                                {editItem?.name ?? ''}
                            </>
                        ) : (
                            <Translate content="failoverDomain.create.title" />
                        )}
                    </DialogTitle>
                </DialogHeaderLeft>
            </DialogHeader>
            <DialogBody>
                <FormPanel>
                    <Form
                        id="failoverDomainForm"
                        footerClassName="hidden"
                        submitButton={false}
                        cancelButton={false}
                        applyButton={false}
                        hideRequiredLegend={true}
                        vertical={true}
                        values={formValues}
                        errors={formErrors}
                    >
                        <FormFieldText
                            name="name"
                            size={SIZE.FILL}
                            label={<Translate content="failoverDomain.create.form.name" />}
                            required={true}
                            onChange={(value: string) => setFormValues(prev => ({ ...prev, name: value }))}
                            data-cy="failover-domain-name"
                        />
                        <FormFieldText
                            name="description"
                            size={SIZE.FILL}
                            label={<Translate content="failoverDomain.create.form.description" />}
                            multiline={true}
                            rows={3}
                            onChange={(value: string) => setFormValues(prev => ({ ...prev, description: value }))}
                            data-cy="failover-domain-description"
                        />
                        <FormField
                            name="location_id"
                            required={true}
                            label={<Translate content="failoverDomain.create.form.location" />}
                        >
                            {({ getId }) => (
                                <AsyncSelectInput
                                    inputId={getId()}
                                    menuPosition="fixed"
                                    loadOptions={loadLocationOptions}
                                    onChange={handleLocationChange}
                                    debounceTimeout={1000}
                                    additional={{ page: 1 }}
                                    value={locationOption}
                                    isDisabled={currentStep === 2 || isEditMode}
                                    data-cy="failover-domain-location"
                                />
                            )}
                        </FormField>
                        <FormField
                            name="storage_id"
                            required={true}
                            label={<Translate content="failoverDomain.create.form.storage" />}
                        >
                            {({ getId }) => {
                                const isStorageDisabled = currentStep === 2 || isEditMode || formValues.location_id === 0;
                                const selectInput = (
                                    <AsyncSelectInput
                                        inputId={getId()}
                                        menuPosition="fixed"
                                        loadOptions={loadStorageOptions}
                                        onChange={handleStorageChange}
                                        debounceTimeout={1000}
                                        additional={{ page: 1 }}
                                        value={storageOption}
                                        isDisabled={isStorageDisabled}
                                        key={formValues.location_id}
                                        data-cy="failover-domain-storage"
                                    />
                                );

                                if (formValues.location_id === 0 && !isEditMode) {
                                    return (
                                        <Tooltip title={<Translate content="failoverDomain.create.form.storageDisabledTooltip" />}>
                                            <div>{selectInput}</div>
                                        </Tooltip>
                                    );
                                }

                                return selectInput;
                            }}
                        </FormField>
                    </Form>
                    {generalErrorMessages.length > 0 && (
                        <StatusMessage intent={INTENT_TYPE.DANGER} data-cy="failover-domain-form-error">
                            {generalErrorMessages.map(message => (
                                <div key={message}>{message}</div>
                            ))}
                        </StatusMessage>
                    )}
                    <ButtonGroup>
                        {renderFooterButtons()}
                    </ButtonGroup>
                </FormPanel>
                <ContentPanel>
                    {hasStorageAndLocation ? (
                        <>
                            <ContentHeader>
                                <StepBreadcrumb
                                    currentStep={currentStep}
                                    selectedCrCount={selectedCrIds.length}
                                    selectedIpBlockCount={selectedIpBlockIds.length}
                                    onBack={handleBack}
                                />
                                <SearchContainer>
                                    <Input
                                        size={SIZE.FILL}
                                        placeholder={translate('failoverDomain.create.search.placeholder') as string}
                                        value={searchQuery}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                                        prefix={<Icon name={ICONS.SEARCH} />}
                                        data-cy="failover-domain-search"
                                    />
                                </SearchContainer>
                            </ContentHeader>
                            {currentStep === 1 ? (
                                <ComputeResourceStep
                                    storageId={formValues.storage_id}
                                    locationId={formValues.location_id}
                                    selectedIds={selectedCrIds}
                                    editingDomainId={editItem?.id}
                                    searchQuery={searchQuery}
                                    onSelectionChange={setSelectedCrIds}
                                />
                            ) : (
                                <IpBlockStep
                                    computeResourceIds={selectedCrIds}
                                    failoverDomainId={editItem?.id}
                                    selectedIds={selectedIpBlockIds}
                                    searchQuery={searchQuery}
                                    onSelectionChange={setSelectedIpBlockIds}
                                />
                            )}
                        </>
                    ) : (
                        <Placeholder>
                            <Translate content="failoverDomain.create.placeholder" />
                        </Placeholder>
                    )}
                </ContentPanel>
            </DialogBody>
        </DialogOverlay>
    );
};

const mapStateToProps = (state: RootState) => ({
    isSubmitting: state.app.loadingFlags.has(LOADING_FLAGS.FAILOVER_DOMAIN_CREATE)
        || state.app.loadingFlags.has(LOADING_FLAGS.FAILOVER_DOMAIN_UPDATE),
    formErrors: getProcessedErrors(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    failoverDomainActions: bindActionCreators(failoverDomainActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(FailoverDomainCreateDialog);
